import { createLogger } from "@dransay/logger/browser";
import * as Sentry from "@sentry/react-router";
import { triggerGracefulReload } from "./graceful-reload";
import { isVersionMismatchError } from "./version-mismatch-handler";

const logger = createLogger({ service: "platform" });

/**
 * Network connection information for diagnostics.
 */
interface NetworkInfo {
	online: boolean;
	effectiveType?: string;
	downlink?: number;
	rtt?: number;
	saveData?: boolean;
}

/**
 * Extended navigator with Network Information API support.
 */
interface NavigatorWithConnection extends Navigator {
	connection?: {
		effectiveType?: string;
		downlink?: number;
		rtt?: number;
		saveData?: boolean;
	};
}

/**
 * Error patterns that indicate a network/fetch failure.
 * These errors occur during React Router navigation when fetch fails.
 */
const NETWORK_ERROR_PATTERNS = [
	/Load failed/i,
	/Failed to fetch/i,
	/NetworkError/i,
	/Network request failed/i,
	/The Internet connection appears to be offline/i,
	/A server with the specified hostname could not be found/i,
	/The network connection was lost/i,
	/net::ERR_/i,
	/ECONNREFUSED/i,
	/ETIMEDOUT/i,
];

/**
 * Checks if an error is a network-related fetch failure.
 */
export function isNetworkError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;

	const message = error.message;
	const name = error.name;

	if (
		name === "TypeError" &&
		NETWORK_ERROR_PATTERNS.some((p) => p.test(message))
	) {
		return true;
	}

	return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Captures current network connection information.
 */
function getNetworkInfo(): NetworkInfo {
	const nav = navigator as NavigatorWithConnection;
	const connection = nav.connection;

	return {
		online: navigator.onLine,
		effectiveType: connection?.effectiveType,
		downlink: connection?.downlink,
		rtt: connection?.rtt,
		saveData: connection?.saveData,
	};
}

/**
 * Extracts the URL from a fetch error if available.
 */
function extractFailedUrl(error: Error): string | undefined {
	const stack = error.stack ?? "";
	const urlMatch = stack.match(/https?:\/\/[^\s"')]+/);
	return urlMatch?.[0];
}

/**
 * Gets the current page visibility state.
 */
function getVisibilityState(): string {
	return document.visibilityState;
}

/**
 * Reports a network error to Sentry with rich diagnostic context.
 */
export function reportNetworkError(error: Error): void {
	const networkInfo = getNetworkInfo();
	const failedUrl = extractFailedUrl(error);
	const visibilityState = getVisibilityState();

	Sentry.addBreadcrumb({
		category: "network-error",
		message: "Network fetch failed",
		level: "error",
		data: {
			errorMessage: error.message,
			errorName: error.name,
			online: networkInfo.online,
			effectiveType: networkInfo.effectiveType,
			failedUrl,
			visibilityState,
		},
	});

	Sentry.captureException(error, {
		tags: {
			errorType: "network-fetch",
			online: String(networkInfo.online),
			effectiveType: networkInfo.effectiveType ?? "unknown",
			visibilityState,
		},
		extra: {
			networkInfo,
			failedUrl,
			currentUrl: window.location.href,
			userAgent: navigator.userAgent,
			timestamp: new Date().toISOString(),
		},
		fingerprint: [
			"network-error",
			networkInfo.online ? "online" : "offline",
			error.message.slice(0, 50),
		],
	});
}

/**
 * Handles network errors by reporting to Sentry and triggering recovery.
 * If user is online, triggers a page reload to recover from transient failures.
 * Returns true if the error was handled, false otherwise.
 */
export function handleNetworkError(error: unknown): boolean {
	if (
		!(error instanceof Error) ||
		isVersionMismatchError(error) ||
		!isNetworkError(error)
	) {
		return false;
	}

	reportNetworkError(error);

	logger.warn(
		{
			message: error.message,
			online: navigator.onLine,
			visibility: document.visibilityState,
		},
		"Fetch failed",
	);

	// If user is online, attempt recovery via page reload
	// If offline, don't reload as it will just fail again
	if (navigator.onLine) {
		triggerGracefulReload(error, {
			category: "network-error",
			storageKey: "network-error-reload",
		});
	}

	return true;
}

/**
 * Sets up global handlers for network errors during fetch operations.
 * Catches errors that occur outside of React's error boundary.
 * Prevents error propagation to avoid showing crash screens for transient network issues.
 */
export function setupNetworkErrorHandler(): void {
	window.addEventListener("unhandledrejection", (event) => {
		const error = event.reason;
		if (isNetworkError(error)) {
			// Prevent the error from propagating to React's ErrorBoundary
			event.preventDefault();
			handleNetworkError(error);
		}
	});

	window.addEventListener("error", (event) => {
		const error = event.error;
		if (error && isNetworkError(error)) {
			// Prevent the error from propagating to React's ErrorBoundary
			event.preventDefault();
			handleNetworkError(error);
		}
	});

	window.addEventListener("online", () => {
		Sentry.addBreadcrumb({
			category: "network-status",
			message: "Browser came online",
			level: "info",
		});
	});

	window.addEventListener("offline", () => {
		Sentry.addBreadcrumb({
			category: "network-status",
			message: "Browser went offline",
			level: "warning",
		});
	});
}
